import { useMemo } from 'react';

import { User } from 'entities';

import { UserAvatar } from 'components/UserAvatar';
import { Column, Row, Tooltip } from 'components/Blocks';

import { useTaskModal } from '../../context.taskModal';

import { formatPublishedAtDate, dateDistanceToNow } from 'utils/date';
import { REGEX } from 'utils/constants';
import { cloneRegexWithExtraFlag } from 'utils/regex';
import { isValidLink } from 'utils/link';

interface TaskLink {
  timestamp: string;
  id: string;
  user: User;
  link: string;
}

export function TaskLinks() {
  const { task } = useTaskModal();

  const links = useMemo(() => {
    if (!task.comments) return [] as TaskLink[];

    return task.comments
      .map(({ text, ...rest }) => {
        // we do this because we need a new instance of a global
        // regex every time, as the g flag can make matches inconsistent
        const links = text.match(cloneRegexWithExtraFlag(REGEX.LINK, 'g'))?.filter(isValidLink);

        return (
          links?.map((link, index) => ({
            ...rest,
            id: `${rest.id}_${link}_${index}`,
            link,
            user: task.comments[0].user,
          })) || []
        );
      })
      .flat() as TaskLink[];
  }, [task.comments]);

  return (
    <Column gap="6">
      {links.map(({ user, id, timestamp, link }) => (
        <Row key={id}>
          <UserAvatar user={user} />

          <Column gap="1" fullWidth>
            <Row justifyContent="space-between">
              <span className="font-semibold">{user.name}</span>

              <Tooltip content={formatPublishedAtDate(timestamp)}>
                <span className="text-xs font-medium">{dateDistanceToNow(timestamp)}</span>
              </Tooltip>
            </Row>

            <div className="rounded-md bg-muted p-2">
              <a
                target="_blank"
                rel="noopener noreferrer"
                draggable={false}
                href={link}
                className="w-fit break-words text-sm text-primary transition-colors hover:text-primary-light"
              >
                {link}
              </a>
            </div>
          </Column>
        </Row>
      ))}
    </Column>
  );
}
